import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../AuthContext";

const API_BASE = process.env.REACT_APP_API_URL || "";
// Shared between AdminDisputes and AdminUsers so a Basic Auth session carries
// across both pages.
const STORAGE_KEY = "disputefai_admin_auth";

export function encodeBasic(user, pass) {
  return "Basic " + btoa(`${user}:${pass}`);
}

// Resolves the Authorization header for the Basic-Auth admin console two
// ways: (1) a Basic Auth session already in sessionStorage, or (2)
// auto-detected by probing `probePath` with the current user's JWT (Bearer
// token) — the backend accepts that for accounts with users.is_admin = 1
// (see requireAdminAccess in backend/src/routes/ai.js). Falls back to the
// manual Basic Auth login form when neither applies.
export function useAdminAuth(probePath) {
  const { token } = useAuth();

  const [basicHeader, setBasicHeader] = useState(() => sessionStorage.getItem(STORAGE_KEY) || null);
  const [loginUser, setLoginUser] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginError, setLoginError] = useState(null);

  // JWT auto-login: probed once per token, only when there's no Basic
  // session already active and the user hasn't dismissed it (logout).
  const [jwtHeader, setJwtHeader] = useState(null);
  const [jwtChecked, setJwtChecked] = useState(false);
  const [jwtDismissed, setJwtDismissed] = useState(false);

  useEffect(() => {
    if (basicHeader || !token || jwtDismissed) {
      setJwtChecked(true);
      return;
    }
    let cancelled = false;
    setJwtChecked(false);
    const header = `Bearer ${token}`;
    fetch(`${API_BASE}${probePath}`, { headers: { Authorization: header } })
      .then((res) => {
        if (cancelled) return;
        if (res.ok) setJwtHeader(header);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setJwtChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [basicHeader, token, jwtDismissed, probePath]);

  const authHeader = basicHeader || jwtHeader;
  // True while a JWT admin session might still apply and we don't know yet —
  // callers should show a loading state instead of the login form.
  const resolving = !basicHeader && !!token && !jwtChecked;

  // Stable identities (useCallback) — these get passed down into pages'
  // load() as a useCallback dependency; a fresh identity every render would
  // re-trigger their "run load() when authHeader is set" effect on every
  // render, hammering the endpoint in a loop.
  const handleLogin = useCallback(
    (e) => {
      e.preventDefault();
      setLoginError(null);
      if (!loginUser || !loginPass) {
        setLoginError("Username and password are required.");
        return;
      }
      const header = encodeBasic(loginUser, loginPass);
      sessionStorage.setItem(STORAGE_KEY, header);
      setBasicHeader(header);
      setLoginPass("");
    },
    [loginUser, loginPass]
  );

  const handleAdminLogout = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setBasicHeader(null);
    setJwtHeader(null);
    setJwtDismissed(true);
    setLoginUser("");
    setLoginPass("");
    setLoginError(null);
  }, []);

  // Call from the page's load() on a 401 — bad Basic creds specifically (a
  // failed JWT probe never surfaces as authHeader in the first place, so it
  // can't reach here).
  const handleUnauthorized = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setBasicHeader(null);
    setLoginError("Invalid admin credentials.");
  }, []);

  return {
    authHeader,
    resolving,
    loginUser,
    setLoginUser,
    loginPass,
    setLoginPass,
    loginError,
    handleLogin,
    handleAdminLogout,
    handleUnauthorized,
  };
}
